/**
 * Tiny inline bar trend for table cells — Field Notes chart set.
 *
 * Fixed-pixel svg (no viewBox stretching) so bars stay crisp at table
 * cell scale. `barClassName` is a closed union over the fixed chart
 * ramp of DESIGN.md §2 (forest → moss → rust → warn → line-strong) —
 * same discipline as `StackedBars`, which takes no color prop at all:
 * a caller cannot drift a chart off-token. The default is the ramp's
 * first slot.
 *
 * Accessibility: the svg is exposed as ONE image (`role="img"` +
 * mandatory `ariaLabel` + `<title>`); the bars are decoration. The
 * surrounding table row must carry the real numbers as text — a
 * sparkline is never the sole carrier of a value (DESIGN.md §9).
 *
 * Edges: empty `values` render a blank strip of `height`; an all-zero
 * series renders floor-height stubs so "present but idle" stays
 * visibly different from "no data".
 */

const BAR_WIDTH = 4;
const BAR_GAP = 2;
const MIN_BAR_HEIGHT = 1.5;

interface Props {
  readonly values: readonly number[];
  /** Pixel height of the strip. */
  readonly height?: number;
  readonly barClassName?:
    | 'fill-forest'
    | 'fill-moss'
    | 'fill-rust'
    | 'fill-warn'
    | 'fill-line-strong';
  readonly ariaLabel: string;
  readonly testId?: string;
}

export function Sparkline({
  values,
  height = 24,
  barClassName = 'fill-forest',
  ariaLabel,
  testId,
}: Props): JSX.Element {
  const max = Math.max(1, ...values);
  const width = values.length === 0 ? BAR_WIDTH : values.length * (BAR_WIDTH + BAR_GAP) - BAR_GAP;
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block"
      data-testid={testId}
    >
      <title>{ariaLabel}</title>
      {values.map((value, index) => {
        const barHeight = Math.max(MIN_BAR_HEIGHT, (Math.max(0, value) / max) * height);
        return (
          <rect
            key={index}
            x={index * (BAR_WIDTH + BAR_GAP)}
            y={height - barHeight}
            width={BAR_WIDTH}
            height={barHeight}
            rx={1}
            className={barClassName}
          />
        );
      })}
    </svg>
  );
}
